import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  RICH_TEXT_EDITOR_INIT,
  TINYMCE_SCRIPT_PATH,
} from '../../components/common/rich-text-editor-dialog/rich-text-editor.config';

/** Minimal shape of the global `tinymce` object we touch during warm-up. */
interface TinyMceGlobal {
  init(config: Record<string, unknown>): unknown;
}

/** Minimal shape of the throwaway editor instance we tear down after init. */
interface WarmupEditor {
  on(event: string, handler: () => void): void;
  remove(): void;
}

/**
 * Warms the self-hosted TinyMCE runtime **once per app session**, in the
 * background, so the first real pop-out open is fast.
 *
 * ## Why
 * The editor is self-hosted (`/tinymce/*`) and lazily mounted, so the FIRST
 * open triggers a waterfall of asset requests — core script (~469 KB) + the
 * silver theme + dom model + oxide skin CSS + the configured plugins — before
 * the editor appears. That waterfall is the "brief seconds" lag. This service
 * pays it up front: it loads the script and runs ONE throwaway editor init on a
 * detached, off-screen node (which fetches the exact same theme/model/skin/
 * plugin assets the real editor uses, via the shared {@link RICH_TEXT_EDITOR_INIT}),
 * then tears it down. After warming, `window.tinymce` is ready and every asset
 * is browser-cached, so the user's first open only pays the cheap ~100 ms
 * re-init (which the dialog's skeleton already masks).
 *
 * ## Scope — global via the shared seam, lazy + idempotent
 * Called from the shared {@link RichTextFieldComponent} on mount, so **every**
 * sheet that uses the rich-text field (roles 23, 24, and any future migration)
 * inherits warming with no per-sheet wiring. The {@link warmed} guard makes
 * repeat calls — many fields on one sheet, re-navigation — no-ops. It is
 * lazy/targeted on purpose: it only runs once a user actually reaches a
 * rich-text sheet, so users who never open an editor never download TinyMCE.
 *
 * Best-effort: any failure (script blocked, init error) just means the first
 * open pays the normal cost — it never throws into the caller.
 */
@Injectable({ providedIn: 'root' })
export class TinyMceWarmupService {
  private started = false;
  private idleHandle: number | null = null;
  private idleScheduled = false; // idleHandle is a requestIdleCallback id (vs a setTimeout id)
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Kick off the one-time background warm-up. Idempotent across the session.
   *
   * @param eager When `true` — a strong "about to open" signal such as the user
   *   hovering or focusing a preview — run **immediately** instead of waiting
   *   for the browser-idle slot the on-mount call uses. This closes the gap
   *   where a heavy sheet (e.g. role 24's large table) delays
   *   `requestIdleCallback` past a quick hover→click, which would otherwise let
   *   the first open pay the full asset waterfall. Calling eager after the idle
   *   warm-up has already started is a harmless no-op.
   */
  warm(eager = false): void {
    if (this.started || !this.isBrowser) return;

    if (eager) {
      // Jump the idle queue: cancel any pending idle/timeout and run now.
      this.cancelPending();
      this.started = true;
      this.run();
      return;
    }

    if (this.idleHandle !== null) return; // already scheduled for idle

    // Run when the browser is idle so warming never competes with the sheet's
    // own first render. requestIdleCallback isn't universal (older Safari), so
    // fall back to a near-immediate timeout.
    const win = window as typeof window & {
      requestIdleCallback?: (cb: () => void) => number;
    };
    if (typeof win.requestIdleCallback === 'function') {
      this.idleScheduled = true;
      this.idleHandle = win.requestIdleCallback(() => this.runFromSchedule());
    } else {
      this.idleScheduled = false;
      this.idleHandle = window.setTimeout(() => this.runFromSchedule(), 1);
    }
  }

  /** Idle/timeout callback entry: clear the handle, then run once. */
  private runFromSchedule(): void {
    this.idleHandle = null;
    if (this.started) return;
    this.started = true;
    this.run();
  }

  /**
   * Cancel a still-pending idle/timeout warm-up — used when an eager (hover)
   * call jumps ahead of the queued on-mount one.
   */
  private cancelPending(): void {
    if (this.idleHandle === null) return;
    const win = window as typeof window & {
      cancelIdleCallback?: (handle: number) => void;
    };
    if (this.idleScheduled && typeof win.cancelIdleCallback === 'function') {
      win.cancelIdleCallback(this.idleHandle);
    } else {
      window.clearTimeout(this.idleHandle);
    }
    this.idleHandle = null;
  }

  private run(): void {
    this.loadScript()
      .then((tinymce) => {
        if (!tinymce) return;

        // A detached, off-screen host so the throwaway editor never flashes or
        // affects layout. It is removed the instant init completes.
        const host = document.createElement('div');
        host.setAttribute('aria-hidden', 'true');
        host.style.cssText =
          'position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden';
        const target = document.createElement('textarea');
        host.appendChild(target);
        document.body.appendChild(host);

        tinymce.init({
          ...RICH_TEXT_EDITOR_INIT,
          target,
          setup: (editor: WarmupEditor) => {
            editor.on('init', () => {
              // All assets are fetched + cached now; drop the warm-up editor —
              // but NOT from inside this handler.
              //
              // `setup` runs in the Editor constructor, so our listener is
              // registered before every other `init` listener and runs first.
              // TinyMCE's DOM model registers its own `init` listener during
              // render (the table object-resizing setup) which opens with
              // `SugarElement.fromDom(editor.getBody())`, and `Editor.destroy()`
              // nulls `bodyElement`/`contentDocument`/`contentWindow` — so
              // `getBody()` returns null the moment `remove()` completes.
              // Tearing down synchronously here handed that listener a null body
              // and threw `Node cannot be null or undefined` (surfacing as an
              // unhandled rejection, since TinyMCE dispatches `Init` from inside
              // a `.then()`). TinyMCE also keeps using the same instance after
              // the dispatch returns — `focus()`, `nodeChanged({initial:true})`.
              //
              // One macrotask is enough: it lets the whole `Init` dispatch and
              // TinyMCE's init tail finish against a live editor, and costs
              // nothing — the assets we came for are already cached by now.
              setTimeout(() => {
                try {
                  editor.remove();
                } catch {
                  /* best-effort teardown */
                }
                host.remove();
              });
            });
          },
        });
      })
      .catch(() => {
        // Warm-up is best-effort. Allow a later mount/hover to retry.
        this.started = false;
      });
  }

  /**
   * Resolve the global `tinymce`, loading the self-hosted script if it isn't on
   * the page yet. Reuses an existing `<script>` (e.g. one the editor component
   * already added) rather than adding a duplicate.
   */
  private loadScript(): Promise<TinyMceGlobal | null> {
    const win = window as typeof window & { tinymce?: TinyMceGlobal };
    if (win.tinymce) return Promise.resolve(win.tinymce);

    return new Promise<TinyMceGlobal | null>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${TINYMCE_SCRIPT_PATH}"]`,
      );
      if (existing) {
        if (win.tinymce) {
          resolve(win.tinymce);
          return;
        }
        existing.addEventListener('load', () => resolve(win.tinymce ?? null));
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = TINYMCE_SCRIPT_PATH;
      script.addEventListener('load', () => resolve(win.tinymce ?? null));
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }
}
